import { useState } from 'react';
import PropTypes from 'prop-types';
import { Confirm, useDeleteMany, useListContext, useNotify, usePermissions, useRecordContext, useResourceContext, useTranslate } from 'react-admin';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomDeleteModalButton from '@/components/global/buttons/CustomDeleteModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import { DetailsActionsPopover } from '@/components/global/buttons/DetailsActionsPopover.jsx';
import { CustomConfirmContent } from '@/components/global/buttons/CustomConfirmContent';
import { globalStyles } from '@/themes/commonStyles/global.js';
import { useDrawer } from '@/composables/useDrawer';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import { isAdStatusEditable } from '@/constants/AdStatus.js';

export const AdsCampaignMoreActions = ({ variant = 'list', handleClose }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    const resource = useResourceContext();
    const notify = useNotify();
    const { refetch } = useListContext();
    const { permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const canManageAd = (permissions?.isAdmin || permissions?.isClient) && !permissions?.isPosOwner;
    const canShowAd = canViewDetails('ad', permissions);
    const canEditAd = canManageAd && isAdStatusEditable(record?.status);

    const [deleteMany, { isLoading: isDeleting }] = useDeleteMany(
        resource || 'ad',
        { ids: record?.id ? [record.id] : [] },
        {
            onSuccess: () => {
                notify(translate('common.operations.bulkDeleted'));
                refetch();
                if (variant === 'details') {
                    handleClose?.();
                }
            },
            onError: error => {
                notify(error.message, { type: 'error' });
            },
        },
    );

    if (!record || (variant === 'list' && !canShowAd && !canManageAd) || (variant === 'details' && !canManageAd)) {
        return null;
    }

    const handleDeleteClick = () => setIsDeleteConfirmOpen(true);
    const handleDeleteDialogClose = () => setIsDeleteConfirmOpen(false);
    const handleDeleteConfirm = () => {
        deleteMany();
        setIsDeleteConfirmOpen(false);
    };

    const deleteButton = canManageAd ? (
        <CustomDeleteModalButton onOpen={handleDeleteClick} label={translate('resources.adsCampaign.labels.deleteAdvertisement')} />
    ) : null;

    const confirmDialog = canManageAd ? (
        <Confirm
            isOpen={isDeleteConfirmOpen}
            loading={isDeleting}
            content={
                <CustomConfirmContent
                    content={translate('ra.message.delete_content')}
                    handleDialogClose={handleDeleteDialogClose}
                    handleConfirm={handleDeleteConfirm}
                    loading={isDeleting}
                />
            }
            onConfirm={handleDeleteConfirm}
            onClose={handleDeleteDialogClose}
            sx={{ ...globalStyles.customConfirmDialog }}
        />
    ) : null;

    if (variant === 'details') {
        return (
            <>
                <DetailsActionsPopover>
                    {canEditAd ? (
                        <CustomEditModalButton
                            label={translate('resources.adsCampaign.labels.editAdvertisement')}
                            onOpen={() => openDrawer(record.id, 'ad-edit')}
                        />
                    ) : null}
                    {deleteButton}
                </DetailsActionsPopover>
                {confirmDialog}
            </>
        );
    }

    return (
        <>
            <CustomMorePopover record={record} color='secondary'>
                {canShowAd ? (
                    <CustomShowModalButton
                        label={translate('resources.adsCampaign.labels.showAdvertisement')}
                        onOpen={() => openDrawer(record.id, 'ad-show')}
                    />
                ) : null}
                {deleteButton}
            </CustomMorePopover>
            {confirmDialog}
        </>
    );
};

AdsCampaignMoreActions.propTypes = {
    variant: PropTypes.oneOf(['list', 'details']),
    handleClose: PropTypes.func,
};
